/**
 * Data Table Component
 *
 * Sortable, virtualized table for displaying time series data.
 * Supports multiple variables and CSV export.
 */

import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import type { TimeSeries } from '@/simulation/parsers/types';
import type { UnitSystem } from '../utils/unitConversion';
import { convertValue, getDisplayUnit, formatNumber } from '../utils/unitConversion';
import { downloadFile } from '../utils/export';

/**
 * Column definition
 */
interface Column {
  id: string;
  label: string;
  accessor: (row: DataRow) => string | number;
  sortable?: boolean;
  width?: number;
  align?: 'left' | 'center' | 'right';
}

/**
 * Data row structure
 */
interface DataRow {
  id: string;
  dateTime: string;
  month: number;
  day: number;
  hour: number;
  minute: number;
  dayOfSimulation: number;
  values: Record<string, number>;
}

/**
 * Sort direction
 */
type SortDirection = 'asc' | 'desc';

/**
 * Props for DataTable component
 */
interface DataTableProps {
  /** Time series data to display */
  series: Array<{
    id: string;
    name: string;
    timeSeries: TimeSeries;
  }>;
  /** Unit system (SI or IP) */
  unitSystem?: UnitSystem;
  /** Row height for virtualization */
  rowHeight?: number;
  /** Table height */
  height?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Virtual scroll hook for efficient rendering of large datasets
 */
function useVirtualScroll(
  itemCount: number,
  itemHeight: number,
  containerHeight: number
) {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleCount = Math.ceil(containerHeight / itemHeight) + 2;
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - 1);
  const endIndex = Math.min(itemCount, startIndex + visibleCount);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  return {
    startIndex,
    endIndex,
    handleScroll,
    totalHeight: itemCount * itemHeight,
    offsetY: startIndex * itemHeight,
  };
}

/**
 * Sort icon component
 */
function SortIcon({ direction }: { direction: SortDirection | null }) {
  if (!direction) {
    return (
      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  return (
    <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      {direction === 'asc' ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      )}
    </svg>
  );
}

/**
 * Data Table Component
 */
export function DataTable({
  series,
  unitSystem = 'SI',
  rowHeight = 36,
  height = 400,
  className = '',
}: DataTableProps) {
  const [sortColumn, setSortColumn] = useState<string>('dateTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(height - 48); // Subtract header height

  // Update container height on resize
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => { window.removeEventListener('resize', updateHeight); };
  }, []);

  // Build columns
  const columns = useMemo(() => {
    const cols: Column[] = [
      {
        id: 'dateTime',
        label: 'Date/Time',
        accessor: (row) => row.dateTime,
        sortable: true,
        width: 120,
        align: 'left',
      },
    ];

    for (const s of series) {
      const unit = getDisplayUnit(s.timeSeries.variable.units, unitSystem);
      cols.push({
        id: s.id,
        label: `${s.name}${unit ? ` [${unit}]` : ''}`,
        accessor: (row) => {
          const value = row.values[s.id];
          if (value === undefined) return '-';
          const converted = convertValue(value, s.timeSeries.variable.units, unitSystem);
          return formatNumber(converted, 2);
        },
        sortable: true,
        width: 150,
        align: 'right',
      });
    }

    return cols;
  }, [series, unitSystem]);

  // Build rows from time series data
  const rows = useMemo(() => {
    if (series.length === 0) return [];

    // Use the first series as the base for timestamps
    const baseSeries = series[0];
    if (!baseSeries) return [];
    const rowMap = new Map<string, DataRow>();

    for (const point of baseSeries.timeSeries.data) {
      const ts = point.timestamp;
      const key = `${String(ts.month)}-${String(ts.day)}-${String(ts.hour)}-${String(ts.minute)}`;
      const dateTime = `${ts.month.toString().padStart(2, '0')}/${ts.day.toString().padStart(2, '0')} ${ts.hour.toString().padStart(2, '0')}:${ts.minute.toString().padStart(2, '0')}`;

      rowMap.set(key, {
        id: key,
        dateTime,
        month: ts.month,
        day: ts.day,
        hour: ts.hour,
        minute: ts.minute,
        dayOfSimulation: ts.dayOfSimulation,
        values: { [baseSeries.id]: point.value },
      });
    }

    // Add values from other series
    for (let i = 1; i < series.length; i++) {
      const s = series[i];
      if (!s) continue;
      for (const point of s.timeSeries.data) {
        const ts = point.timestamp;
        const key = `${String(ts.month)}-${String(ts.day)}-${String(ts.hour)}-${String(ts.minute)}`;
        const row = rowMap.get(key);
        if (row) {
          row.values[s.id] = point.value;
        }
      }
    }

    return Array.from(rowMap.values());
  }, [series]);

  // Sort rows
  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    const column = columns.find((c) => c.id === sortColumn);
    if (!column) return sorted;

    sorted.sort((a, b) => {
      let aVal: string | number = column.accessor(a);
      let bVal: string | number = column.accessor(b);

      // Handle numeric comparison
      if (typeof aVal === 'string' && !isNaN(parseFloat(aVal))) {
        aVal = parseFloat(aVal);
      }
      if (typeof bVal === 'string' && !isNaN(parseFloat(bVal))) {
        bVal = parseFloat(bVal);
      }

      // Handle date/time comparison
      if (sortColumn === 'dateTime') {
        // Sort by month, day, hour, minute
        const cmp =
          a.month - b.month ||
          a.day - b.day ||
          a.hour - b.hour ||
          a.minute - b.minute;
        return sortDirection === 'asc' ? cmp : -cmp;
      }

      // Standard comparison
      let cmp = 0;
      if (aVal < bVal) cmp = -1;
      if (aVal > bVal) cmp = 1;
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return sorted;
  }, [rows, sortColumn, sortDirection, columns]);

  // Virtual scroll
  const { startIndex, endIndex, handleScroll, totalHeight, offsetY } =
    useVirtualScroll(sortedRows.length, rowHeight, containerHeight);

  // Handle column header click
  const handleHeaderClick = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = columns.map((c) => c.label).join(',');
    const dataRows = sortedRows.map((row) =>
      columns.map((c) => c.accessor(row)).join(',')
    );
    const csv = [headers, ...dataRows].join('\n');
    downloadFile(csv, 'simulation-data.csv');
  };

  // Visible rows
  const visibleRows = sortedRows.slice(startIndex, endIndex);

  if (rows.length === 0) {
    return (
      <div className={`bg-card rounded-lg p-4 ${className}`}>
        <h3 className="text-lg font-semibold text-foreground mb-4">Data Table</h3>
        <div className="text-center text-muted-foreground py-8">No data available</div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg overflow-hidden flex flex-col ${className}`} style={{ height }}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Data Table</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {sortedRows.length.toLocaleString()} rows
          </p>
        </div>
        <button
          onClick={handleExportCSV}
          className="px-3 py-1.5 text-sm bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0" role="table" aria-label="Simulation data table" aria-rowcount={sortedRows.length + 1}>
        {/* Table header */}
        <div className="flex bg-background border-b border-border shrink-0" role="row">
          {columns.map((column) => (
            <div
              key={column.id}
              role="columnheader"
              aria-sort={sortColumn === column.id ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
              className={`flex items-center gap-1 px-3 py-2 text-sm font-medium text-muted-foreground ${
                column.sortable ? 'cursor-pointer hover:text-foreground hover:bg-muted/50' : ''
              } transition-colors`}
              style={{
                width: column.width,
                minWidth: column.width,
                justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
              }}
              onClick={() => { if (column.sortable) handleHeaderClick(column.id); }}
              title={column.label}
            >
              <span className="truncate">{column.label}</span>
              {column.sortable && (
                <SortIcon direction={sortColumn === column.id ? sortDirection : null} />
              )}
            </div>
          ))}
        </div>

        {/* Table body with virtual scroll */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto"
          onScroll={handleScroll}
          role="rowgroup"
        >
          <div style={{ height: totalHeight, position: 'relative' }}>
            <div style={{ transform: `translateY(${String(offsetY)}px)` }}>
              {visibleRows.map((row, index) => {
                const rowNum = startIndex + index;
                return (
                  <div
                    key={row.id}
                    role="row"
                    aria-rowindex={rowNum + 2} // +2 because header is row 1, data starts at 2
                    className={`flex ${
                      rowNum % 2 === 0 ? 'bg-card' : 'bg-card/50'
                    } hover:bg-muted/50 transition-colors`}
                    style={{ height: rowHeight }}
                  >
                    {columns.map((column) => {
                      const cellValue = column.accessor(row);
                      const cellValueStr = String(cellValue);
                      return (
                        <div
                          key={column.id}
                          role="cell"
                          className="flex items-center px-3 text-sm text-foreground"
                          style={{
                            width: column.width,
                            minWidth: column.width,
                            justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
                          }}
                          title={cellValueStr.length > 15 ? cellValueStr : undefined}
                        >
                          <span className="truncate">{cellValue}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTable;
